import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    getPlacements as getPlacementsAPI,
    updatePlacement as updatePlacementAPI,
    deletePlacement as deletePlacementAPI,
    RoomPlacement,
} from "@/Data/World";
import ss from '@/Data/Session';

type PlacementState = {
    // Placements by addressid
    placementsByUser: Record<string, RoomPlacement[]>;
    loading: boolean;
    error: string | null;
};

const STORAGE_KEY = 'world:placement';

const loadFromStorage = (): Partial<PlacementState> => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) { return JSON.parse(stored); }
    } catch (e) {
        console.error('Failed to load Placement from storage:', e);
    }
    return {};
};

const saveToStorage = (state: PlacementState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            placementsByUser: state.placementsByUser,
        }));
    } catch (e) {
        console.error('Failed to save Placement to storage:', e);
    }
};

const defaultState: PlacementState = {
    placementsByUser: {},
    loading: false,
    error: null,
};

const placementState = atom<PlacementState>({
    key: 'placementState',
    default: { ...defaultState, ...loadFromStorage() },
});

/**
 * Hook for managing room placements
 */
export const usePlacement = () => {
    const [state, setState] = useRecoilState(placementState);

    const updateState = useCallback((newState: Partial<PlacementState>) => {
        setState(prev => {
            const updated = { ...prev, ...newState };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    const { placementsByUser, loading, error } = state;

    /**
     * Fetch placements for a user
     */
    const fetchPlacements = useCallback(async (addressid: string): Promise<RoomPlacement[]> => {
        updateState({ loading: true, error: null });

        try {
            const placements = await getPlacementsAPI(addressid);

            setState(prev => {
                const updated = {
                    ...prev,
                    placementsByUser: {
                        ...prev.placementsByUser,
                        [addressid]: placements,
                    },
                    loading: false,
                };
                saveToStorage(updated);
                return updated;
            });

            return placements;
        } catch (e) {
            console.error('Failed to fetch placements:', e);
            updateState({ loading: false, error: 'Failed to fetch placements' });
            return [];
        }
    }, [setState, updateState]);

    /**
     * Get cached placements for addressid
     */
    const getPlacements = useCallback((addressid: string): RoomPlacement[] => {
        return placementsByUser[addressid] || [];
    }, [placementsByUser]);

    /**
     * Update or place an ornament
     */
    const updatePlacement = useCallback(async (params: {
        ornamentid: string;
        x: number;
        y: number;
        direction: 'right' | 'left';
    }): Promise<RoomPlacement | null> => {
        const result = await updatePlacementAPI(params);

        if (result) {
            const addressid = ss.getCurrentSession().addressid as string | undefined;
            if (addressid) {
                setState(prev => {
                    const currentPlacements = prev.placementsByUser[addressid] || [];
                    const existingIndex = currentPlacements.findIndex(p => p.ornamentid === params.ornamentid);

                    let newPlacements: RoomPlacement[];
                    if (existingIndex >= 0) {
                        newPlacements = [...currentPlacements];
                        newPlacements[existingIndex] = result;
                    } else {
                        newPlacements = [...currentPlacements, result];
                    }

                    const updated = {
                        ...prev,
                        placementsByUser: {
                            ...prev.placementsByUser,
                            [addressid]: newPlacements,
                        },
                    };
                    saveToStorage(updated);
                    return updated;
                });
            }
        }

        return result;
    }, [setState]);

    /**
     * Remove an ornament from room
     */
    const deletePlacement = useCallback(async (ornamentid: string): Promise<boolean> => {
        const success = await deletePlacementAPI(ornamentid);

        if (success) {
            const addressid = ss.getCurrentSession().addressid as string | undefined;
            if (addressid) {
                setState(prev => {
                    const currentPlacements = prev.placementsByUser[addressid] || [];
                    const filteredPlacements = currentPlacements.filter(p => p.ornamentid !== ornamentid);

                    const updated = {
                        ...prev,
                        placementsByUser: {
                            ...prev.placementsByUser,
                            [addressid]: filteredPlacements,
                        },
                    };
                    saveToStorage(updated);
                    return updated;
                });
            }
        }

        return success;
    }, [setState]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        updateState({ error: null });
    }, [updateState]);

    return {
        // State
        placementsByUser,
        loading,
        error,

        // Actions
        fetchPlacements,
        getPlacements,
        updatePlacement,
        deletePlacement,
        clearError,
    };
};
